import React from 'react';
import { Skeleton } from '@/components/ui/Skeleton';
import { PipelineStage, formatCompactBRL } from '@/hooks/useDashboard';

interface Props {
  stages: PipelineStage[];
  loading?: boolean;
  /** Label direita (ex: "Últimos 90 dias") */
  rightLabel?: string;
}

// Opacidades decrescentes do funil (da etapa 1 para a 5)
const OPACITIES = [0.9, 0.78, 0.66, 0.56, 0.46];

export function PipelineCard({ stages, loading, rightLabel = 'Últimos 90 dias' }: Props) {
  return (
    <section
      aria-labelledby="pipeline-titulo"
      className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-card shadow-sm-warm dark:shadow-sm-dark overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-[18px] pt-4 pb-3 border-b border-line dark:border-line-dark">
        <h3 id="pipeline-titulo" className="text-[14px] font-semibold text-ink-title dark:text-ink-title-dark">
          Pipeline por etapa
        </h3>
        <span className="text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark">
          {rightLabel}
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-3.5 px-[18px] py-4">
        {loading ? (
          <>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i}>
                <div className="flex justify-between">
                  <Skeleton className="h-3 w-44" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="mt-1.5 h-2 w-full" />
              </div>
            ))}
          </>
        ) : (
          stages.map((s, idx) => (
            <div key={s.key}>
              <div className="flex items-baseline justify-between gap-4">
                <div className="min-w-0 flex items-baseline gap-2">
                  <span className="text-[13px] text-ink-text dark:text-ink-text-dark truncate">
                    {s.label}
                  </span>
                  <span className="text-[12px] text-ink-subtitle dark:text-ink-subtitle-dark tabular">
                    · {s.count}
                  </span>
                </div>
                <span className="text-[13px] tabular text-ink-title dark:text-ink-title-dark font-medium">
                  {formatCompactBRL(s.total)}
                </span>
              </div>
              <div
                className="mt-1.5 h-2 w-full rounded bg-surface-alt dark:bg-surface-dark-alt overflow-hidden"
                role="progressbar"
                aria-valuenow={Math.round(s.pct)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${s.label}: ${Math.round(s.pct)}% do pipeline`}
              >
                <div
                  className="h-full bg-brand-500 rounded transition-all"
                  style={{ width: `${s.pct}%`, opacity: OPACITIES[idx] ?? 0.46 }}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
